<template>
    <div
        class="progress-circle"
        role="progressbar"
        :aria-valuenow="progress"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuetext="content ?? t('components.progressCircle.complete', { percent: formatNumber(progress / 100, 'percent') })"
        :data-content="content ?? formatNumber(progress / 100, 'percent')">
        <slot></slot>
    </div>
</template>

<script setup lang="ts">
/* eslint design-system/no-off-scale-spacing: "off" -- The inner-disc margin defines this circular progress chart's ring thickness. */
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

type ProgressCircleProps = {
    progress?: number;
    content?: string;
    gradientFrom?: string;
    gradientTo?: string;
};

const { progress = 0, content, gradientFrom = "var(--color-progress-fill)", gradientTo = "var(--color-progress-track)" } = defineProps<ProgressCircleProps>();

const progressString = computed(() => `${progress}%`);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

div.progress-circle {
    @apply w-full h-full rounded-full flex text-black;
    transition:
        scale var(--motion-duration-slow) var(--motion-ease-standard),
        box-shadow var(--motion-duration-slow) var(--motion-ease-standard);
    background: conic-gradient(v-bind(gradientFrom) v-bind(progressString), v-bind(gradientTo) 0deg);
}

div.progress-circle::after {
    @apply details font-medium flex flex-col justify-center w-full rounded-full bg-white text-center;
    content: attr(data-content);
    margin: 5px;
}

@media (prefers-reduced-motion: reduce) {
    div.progress-circle {
        transition-property: box-shadow;
        scale: none;
    }
}
</style>
